import styled from 'styled-components';

export const MainLayout = styled.div`
  display: flex;
  flex-direction: row;
  min-height: calc(100vh - 70px); /* subtract AppHeader height */
`;

export const ContentArea = styled.div`
  flex: 1;
  height: calc(100vh - 70px);
  overflow-y: auto;
`;

export const AppBackground = styled.main`
  min-height: 100vh;
  background-color: #fafafa;
  width: 100%;
  padding-top: 70px;
`;

export const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  background-color: #fafafa;
  min-height: 100vh;
`;

export const TagButton = styled.div`
  position: absolute;
  top: -10px;
  left: 45px;
  background: linear-gradient(135deg, #009FE3 0%, #003A70 100%);
  color: white;
  padding: 5px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
`;

export const Header = styled.h1`
  color: #2c3e50;
  text-align: center;
  margin-bottom: 2rem;
  font-weight: 300;
  font-size: 2.5rem;
`;

export const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  padding: 2rem;
  margin-bottom: 2rem;
  border: 1px solid #e1e8ed;
  position: relative;
  font-family: 'SF Pro', 'SF Pro Display', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
`;

export const ScrollableWindow = styled.div`
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e1e8ed;
  border-radius: 8px;
  padding: 1.5rem;
  background-color: #f8f9fa;
  margin-bottom: 1.5rem;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

export const ProcessButton = styled.button`
  background: #000000;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: block;
  margin: 0 auto;
  
  &:hover {
    background: #333333;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  }
  
  &:disabled {
    background: #bdc3c7;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

export const ComparisonContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const TextColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

export const ColumnTitle = styled.h3`
  color: #34495e;
  margin-bottom: 1rem;
  font-weight: 500;
  font-size: 1.2rem;
`;

export const MetadataContainer = styled.div`
  margin-bottom: 15px;
  padding: 10px;
  background-color: #f9f9f9;
  border-radius: 4px;
  border: 1px solid #eee;
`;

export const MetadataItem = styled.div`
  font-size: 14px;
  color: #555;
  margin-bottom: 5px;

  strong {
    color: #333;
    margin-right: 8px;
  }
`;

export const TextDisplay = styled.div`
  background: #f8f9fa;
  border: 1px solid #e1e8ed;
  border-radius: 8px;
  padding: 1.5rem;
  min-height: 200px;
  font-family: 'DM Mono', 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.9rem;
  line-height: 1.6;
  white-space: pre-wrap;
  box-sizing: border-box;
  width: 100%;
`;

export const EditableTextArea = styled.textarea<{ $lockResize?: boolean }>`
  background: white;
  border: 1px solid #e1e8ed;
  border-radius: 8px;
  padding: 1.5rem;
  min-height: 200px;
  font-family: 'DM Mono', 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.9rem;
  line-height: 1.6;
  white-space: pre-wrap;
  resize: ${props => props.$lockResize ? 'none' : 'vertical'};
  transition: border-color 0.3s ease, background-color 0.3s ease;
  box-sizing: border-box;
  overflow-y: hidden;
  color: #000000;
  width: 100%;
  
  &:focus {
    outline: none;
    border-color: #009FE3;
    box-shadow: 0 0 0 3px rgba(0, 159, 227, 0.15);
  }
  
  &:disabled {
    background: #f8f9fa;
    color: #000000;
    opacity: 1;
    cursor: not-allowed;
  }
`;

export const LoadingSpinner = styled.div`
  border: 3px solid #f3f3f3;
  border-top: 3px solid #009FE3;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 2rem auto;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export const ErrorMessage = styled.div`
  background: #fee;
  border: 1px solid #fcc;
  color: #c33;
  padding: 1rem;
  border-radius: 8px;
  margin: 1rem 0;
  text-align: center;
`;

export const SuccessMessage = styled.div`
  background: #efe;
  border: 1px solid #cfc;
  color: #3c3;
  padding: 1rem;
  border-radius: 8px;
  margin: 1rem 0;
  text-align: center;
`;

export const InputGroup = styled.div`
  margin-bottom: 1.5rem;
  display: flex;
  flex-direction: column;
`;

export const Label = styled.label`
  margin-bottom: 0.5rem;
  color: #34495e;
  font-weight: 500;
`;

export const InfoCard = styled.div`
  background: #f3f4f6;
  border-radius: 8px;
  padding: 1rem 1.5rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between; // To space out the info and pagination
  border: 1px solid #e5e7eb;
  flex-wrap: wrap;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const InfoLabel = styled.span`
  font-weight: 500;
  color: #34495e;
  margin-right: 0.75rem;
`;

export const InfoValue = styled.span`
  font-weight: 400;
  color: #2c3e50;
  background: white;
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
`;

export const PaginationContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
`;

export const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

export const PageIndicator = styled.span`
  font-weight: 500;
  color: #34495e;
  min-width: 80px;
  text-align: center;
`;

export const PageDotsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 260px;
`;

export const PageDot = styled.button<{ $status: 'unstarted' | 'partial' | 'done'; $active: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  padding: 0;
  cursor: ${({ $status }) => ($status === 'unstarted' ? 'not-allowed' : 'pointer')};
  flex-shrink: 0;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  background: ${({ $status, $active }) => {
    if ($active) return '#009FE3';
    if ($status === 'done') return '#10b981';
    if ($status === 'partial') return '#f59e0b';
    return '#d1d5db';
  }};
  box-shadow: ${({ $active }) => ($active ? '0 0 0 2px rgba(0, 159, 227, 0.4)' : 'none')};

  &:not(:disabled):hover {
    transform: scale(1.4);
  }
`;

export const NavButton = styled.button`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    background: #f8f9fa;
    border-color: #009FE3;
    color: #009FE3;
  }
`;

export const TextInput = styled.input`
  padding: 0.75rem 1rem;
  border: 2px solid #e1e8ed;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;

  &:focus {
    outline: none;
    border-color: #009FE3;
    box-shadow: 0 0 0 3px rgba(0, 159, 227, 0.15);
  }
`;

export const TabContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #f3f4f6;
`;

export const TabButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'isActive' && prop !== 'isDisabled',
})<{ isActive: boolean; isDisabled?: boolean }>`
  padding: 0.75rem 1.5rem;
  border: none;
  background: none;
  cursor: ${props => props.isDisabled ? 'not-allowed' : 'pointer'};
  font-size: 1rem;
  font-weight: 500;
  color: ${props => {
    if (props.isDisabled) return '#bdc3c7';
    return props.isActive ? '#009FE3' : '#5a6e82';
  }};
  border-bottom: 2px solid ${props => (props.isActive ? '#009FE3' : 'transparent')};
  margin-bottom: -2px; // Align with the container's border
  transition: all 0.2s ease;
  opacity: ${props => props.isDisabled ? 0.6 : 1};

  &:hover {
    color: ${props => props.isDisabled ? '#bdc3c7' : '#009FE3'};
  }
`;

export const TooltipContainer = styled.div`
  position: relative;
  display: inline-block;
`;

export const Tooltip = styled.div`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: #2c3e50;
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.85rem;
  white-space: nowrap;
  z-index: 1000;
  margin-bottom: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: #2c3e50;
  }
`;

export const NavButtonWithTooltip = styled(NavButton).withConfig({
  shouldForwardProp: (prop) => prop !== 'isDisabled',
})<{ isDisabled?: boolean }>`
  cursor: ${props => props.isDisabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.isDisabled ? 0.6 : 1};

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    background: ${props => props.isDisabled ? 'white' : '#f8f9fa'};
    border-color: ${props => props.isDisabled ? '#e5e7eb' : '#009FE3'};
    color: ${props => props.isDisabled ? 'inherit' : '#009FE3'};
  }
`;

export const DownloadButton = styled.button`
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    box-shadow: 0 2px 6px rgba(40, 167, 69, 0.25);
    opacity: 0.82;
  }
  
  &:active {
    opacity: 1;
  }
  
  &:disabled {
    background: #bdc3c7;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

export const IconButton = styled.button`
  background: transparent;
  border: none;
  color: #2c3e50;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0.25rem;
  font-size: 24px;
  
  &:hover {
    color: #34495e;
    opacity: 0.75;
  }
  
  &:active {
    opacity: 1;
  }
  
  &:disabled {
    color: #95a5a6;
    cursor: not-allowed;
    transform: none;
  }
`;

export const ManualTypeBar = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 1rem;
`;

export const ManualTypeInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const IconButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const BottomNavigation = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 3rem;
  padding: 2rem 0;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

export const BottomNavButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  background: ${props => props.variant === 'primary' ? '#000000' : 'white'};
  color: ${props => props.variant === 'primary' ? 'white' : '#000000'};
  border: ${props => props.variant === 'primary' ? 'none' : '2px solid #000000'};
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 200px;
  justify-content: center;
  
  &:hover:not(:disabled) {
    background: ${props => props.variant === 'primary' ? '#333333' : '#e8e8e8'};
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
  }
  
  &:disabled {
    background: #bdc3c7;
    color: #7f8c8d;
    border-color: #bdc3c7;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

export const SessionList = styled.ul`
  list-style: none;
  margin: 0.5rem 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const SessionItem = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.6rem 1rem;
  border: 1px solid rgba(225, 232, 237, 0.8);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  background: #fafafa;
  font-family: 'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  font-size: 0.9rem;

  &:hover {
    background: rgba(0, 159, 227, 0.06);
    border-color: rgba(0, 159, 227, 0.25);
    color: #009FE3;
  }
`;

export const DeleteSessionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
  font-size: 0.9rem;
  color: #9ca3af;
  line-height: 1;
  display: flex;
  align-items: center;
  transition: all 0.15s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(239, 68, 68, 0.1);
    color: #ef4444;
  }
`;

export const ConfirmDialogOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

export const ConfirmDialog = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 1.75rem 2rem;
  width: 360px;
  max-width: 90vw;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  font-family: 'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
`;

export const ConfirmDialogActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;

  button {
    padding: 0.5rem 1.2rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    border: 1px solid rgba(225, 232, 237, 0.8);
    background: #f3f4f6;
    color: #374151;
    transition: all 0.15s ease;

    &:hover {
      background: #e5e7eb;
    }

    &.danger {
      background: #ef4444;
      color: #fff;
      border-color: #ef4444;

      &:hover {
        background: #dc2626;
        border-color: #dc2626;
      }
    }
  }
`;
